import { useMemo, useState } from 'react';
import { Button, Card, CardBody, Chip, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';

import useUserStore from '../../../../../common/src/stores/useUserStore';
import { cn } from '../../../../../common/src/utils/cn';
import AccessDeniedPage from '../../access-denied';
import { PageHeader, PageLayout } from '../../../components/ui';
import {
  CARRIER_COLORS,
  PORTS,
  SCHEDULED_VESSELS,
  type ScheduledVessel,
  type ScheduleSort,
} from './mock';

const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sept',
  'oct',
  'nov',
  'dic',
];
const MONTHS_LONG = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const dateForOffset = (offset: number) => {
  const date = startOfToday();
  date.setDate(date.getDate() + offset);
  return date;
};

const formatOffset = (offset: number, includeYear = false) => {
  const date = dateForOffset(offset);
  const value = `${String(date.getDate()).padStart(2, '0')} ${MONTHS_SHORT[date.getMonth()]}`;
  return includeYear ? `${value} ${date.getFullYear()}` : value;
};

const delayFor = (vessel: ScheduledVessel) =>
  vessel.actualEta - vessel.originalEta;

interface TimelineScale {
  start: number;
  end: number;
  span: number;
  percent: (offset: number) => number;
}

const createScale = (): TimelineScale => {
  const start =
    Math.min(
      ...SCHEDULED_VESSELS.map((vessel) =>
        Math.min(vessel.originalEtd, vessel.realEtd)
      )
    ) - 2;
  const end =
    Math.max(
      ...SCHEDULED_VESSELS.map((vessel) =>
        Math.max(vessel.originalEta, vessel.actualEta)
      )
    ) + 3;
  const span = end - start;
  return {
    start,
    end,
    span,
    percent: (offset) => ((offset - start) / span) * 100,
  };
};

const buildMonthBlocks = (scale: TimelineScale) => {
  const months: Array<{
    key: string;
    label: string;
    start: number;
    dayCount: number;
  }> = [];
  let currentKey = '';

  for (let offset = scale.start; offset <= scale.end; offset += 1) {
    const date = dateForOffset(offset);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (key !== currentKey) {
      months.push({
        key,
        label: MONTHS_LONG[date.getMonth()],
        start: offset,
        dayCount: 0,
      });
      currentKey = key;
    }
    months[months.length - 1].dayCount += 1;
  }

  return months.map((month) => ({
    ...month,
    left: scale.percent(month.start),
    width: (month.dayCount / scale.span) * 100,
  }));
};

const buildWeekMarkers = (scale: TimelineScale) => {
  const markers: Array<{ offset: number; left: number; label: string }> = [];
  for (let offset = scale.start; offset <= scale.end; offset += 1) {
    const date = dateForOffset(offset);
    if (date.getDay() !== 1) continue;
    const left = scale.percent(offset);
    const closeToToday = Math.abs(left - scale.percent(0)) < 3.5;
    const closeToStart = left < 2;
    markers.push({
      offset,
      left,
      label:
        closeToToday || closeToStart
          ? ''
          : `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`,
    });
  }
  return markers;
};

const LegendItem = ({
  label,
  kind,
}: {
  label: string;
  kind: 'transit' | 'arrived' | 'pending' | 'transfer' | 'today';
}) => (
  <span className='flex items-center gap-2 text-xs text-default-500'>
    <span
      className={cn(
        'inline-block shrink-0',
        kind === 'today'
          ? 'h-4 w-0.5 rounded bg-brand-700'
          : 'h-2.5 w-5 rounded-full',
        kind === 'transit' && 'bg-brand-700',
        kind === 'arrived' && 'bg-success',
        kind === 'pending' &&
          'border border-dashed border-default-500 bg-default-100',
        kind === 'transfer' &&
          'rounded-sm border border-default-400 bg-default-200'
      )}
      style={
        kind === 'transfer'
          ? {
              backgroundImage:
                'repeating-linear-gradient(45deg, rgba(161,161,170,.55) 0 3px, rgba(161,161,170,.12) 3px 6px)',
            }
          : undefined
      }
    />
    {label}
  </span>
);

const TimelineHeader = ({
  scale,
  months,
  weeks,
}: {
  scale: TimelineScale;
  months: ReturnType<typeof buildMonthBlocks>;
  weeks: ReturnType<typeof buildWeekMarkers>;
}) => (
  <div className='grid grid-cols-[300px_1fr] border-b border-divider bg-default-50'>
    <div className='self-end px-5 pb-3 text-xs font-semibold uppercase tracking-wide text-default-500'>
      Nave / Naviera
    </div>
    <div className='relative h-14 overflow-hidden'>
      {months.map((month) => (
        <span
          key={month.key}
          className='absolute top-2 h-4 overflow-hidden whitespace-nowrap border-l border-divider pl-2 text-[11px] font-bold uppercase tracking-wide text-default-600'
          style={{ left: `${month.left}%`, width: `${month.width}%` }}
        >
          {month.label}
        </span>
      ))}
      {weeks.map((week) => (
        <span
          key={week.offset}
          className='absolute bottom-1.5 -translate-x-1/2 whitespace-nowrap text-[10px] text-default-400'
          style={{ left: `${week.left}%` }}
        >
          {week.label}
        </span>
      ))}
      <span
        className='absolute bottom-1 -translate-x-1/2 rounded-full bg-brand-700 px-2 py-0.5 text-[10px] font-bold text-white'
        style={{ left: `${scale.percent(0)}%` }}
      >
        Hoy
      </span>
    </div>
  </div>
);

const timelineBackground = (
  start: number,
  end: number,
  arrived: boolean,
  pending: boolean,
  pastColor: string,
  futureColor: string
) => {
  if (arrived) return '#17C964';
  if (pending) return 'rgba(113,113,122,.14)';
  const progress =
    0 >= end ? 100 : 0 <= start ? 0 : ((0 - start) / (end - start)) * 100;
  if (progress >= 100) return pastColor;
  if (progress <= 0) return futureColor;
  return `linear-gradient(90deg, ${pastColor} ${progress.toFixed(1)}%, ${futureColor} ${progress.toFixed(1)}%)`;
};

interface VesselRowProps {
  vessel: ScheduledVessel;
  scale: TimelineScale;
  showOriginalPlan: boolean;
  expanded: boolean;
  onToggle: () => void;
}

const VesselRow = ({
  vessel,
  scale,
  showOriginalPlan,
  expanded,
  onToggle,
}: VesselRowProps) => {
  const arrived = vessel.actualEta < 0;
  const pending = vessel.realEtd > 0;
  const inTransit = !arrived && !pending;
  const delay = delayFor(vessel);
  const transfer = vessel.transshipment;
  const widthFor = (start: number, end: number) =>
    ((end - start) / scale.span) * 100;
  const directBackground = timelineBackground(
    vessel.realEtd,
    vessel.actualEta,
    arrived,
    pending,
    '#BE123C',
    'rgba(190,18,60,.28)'
  );
  const displayedVessel = transfer
    ? `${vessel.vessel} → ${transfer.secondVessel}`
    : vessel.vessel;
  const details = [
    ['Ship Name', displayedVessel],
    ['Naviera', vessel.carrier],
    ['POD', `${PORTS[vessel.pod].flag} ${PORTS[vessel.pod].name}`],
    ['Q Embarques', String(vessel.shipmentCount)],
    ['Original ETD', formatOffset(vessel.originalEtd, true)],
    ['Real ETD', formatOffset(vessel.realEtd, true)],
    ['Original ETA', formatOffset(vessel.originalEta, true)],
    ['Actual ETA', formatOffset(vessel.actualEta, true)],
  ];

  return (
    <div className='border-t border-divider'>
      <button
        type='button'
        className={cn(
          'grid w-full grid-cols-[300px_1fr] text-left transition-colors hover:bg-default-100/70',
          expanded && 'bg-default-100/60'
        )}
        onClick={onToggle}
      >
        <div className='min-w-0 px-5 py-3'>
          <div className='flex items-start gap-2'>
            <span className='text-pretty text-sm font-semibold leading-snug'>
              {displayedVessel}
            </span>
            <Chip size='sm' variant='flat' color='danger' className='shrink-0'>
              {vessel.shipmentCount} emb
            </Chip>
          </div>
          <div className='mt-1.5 flex items-center gap-2 text-xs text-default-400'>
            <span
              className='h-2 w-2 shrink-0 rounded-sm'
              style={{
                backgroundColor: CARRIER_COLORS[vessel.carrier] ?? '#A1A1AA',
              }}
            />
            <span className='truncate'>{vessel.carrier}</span>
            <span>·</span>
            <span>{PORTS[vessel.pod].flag}</span>
            <span>{vessel.pod}</span>
          </div>
        </div>

        <div className='relative h-16 overflow-hidden'>
          <span
            className='pointer-events-none absolute inset-y-0 z-10 w-px bg-brand-500/60'
            style={{ left: `${scale.percent(0)}%` }}
            aria-hidden
          />
          {showOriginalPlan && (
            <Tooltip
              content={`Plan original · ETD ${formatOffset(vessel.originalEtd, true)} → ETA ${formatOffset(vessel.originalEta, true)}`}
            >
              <span
                className='absolute top-3 h-2 rounded-full border border-dashed border-default-500 bg-default-100'
                style={{
                  left: `${scale.percent(vessel.originalEtd)}%`,
                  width: `${widthFor(vessel.originalEtd, vessel.originalEta)}%`,
                }}
              />
            </Tooltip>
          )}

          {transfer ? (
            <>
              <Tooltip
                content={`Tramo 1 · ${vessel.vessel} · ETD ${formatOffset(vessel.realEtd, true)} → ${transfer.hubShortName} ${formatOffset(transfer.firstLegEnd, true)}`}
              >
                <span
                  className={cn(
                    'absolute top-7 h-4 rounded-l-full',
                    pending && 'border border-dashed border-default-500'
                  )}
                  style={{
                    left: `${scale.percent(vessel.realEtd)}%`,
                    width: `${widthFor(vessel.realEtd, transfer.firstLegEnd)}%`,
                    background: timelineBackground(
                      vessel.realEtd,
                      transfer.firstLegEnd,
                      arrived,
                      pending,
                      '#BE123C',
                      'rgba(190,18,60,.28)'
                    ),
                  }}
                />
              </Tooltip>
              <Tooltip
                content={`Transbordo en ${transfer.hubName} · ${transfer.secondLegStart - transfer.firstLegEnd} días en puerto`}
              >
                <span
                  className='absolute top-7 h-4 border-y border-default-400'
                  style={{
                    left: `${scale.percent(transfer.firstLegEnd)}%`,
                    width: `${widthFor(transfer.firstLegEnd, transfer.secondLegStart)}%`,
                    backgroundImage:
                      'repeating-linear-gradient(45deg, rgba(161,161,170,.55) 0 3px, rgba(161,161,170,.1) 3px 7px)',
                  }}
                />
              </Tooltip>
              <Tooltip
                content={`Tramo 2 · ${transfer.secondVessel} · ETD ${formatOffset(transfer.secondLegStart, true)} → ETA ${formatOffset(vessel.actualEta, true)}`}
              >
                <span
                  className={cn(
                    'absolute top-7 h-4 rounded-r-full',
                    pending && 'border border-dashed border-default-500'
                  )}
                  style={{
                    left: `${scale.percent(transfer.secondLegStart)}%`,
                    width: `${widthFor(transfer.secondLegStart, vessel.actualEta)}%`,
                    background: timelineBackground(
                      transfer.secondLegStart,
                      vessel.actualEta,
                      arrived,
                      pending,
                      '#F871A0',
                      'rgba(248,113,160,.28)'
                    ),
                  }}
                />
              </Tooltip>
              <span
                className='absolute top-12 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold text-default-400'
                style={{
                  left: `${scale.percent((transfer.firstLegEnd + transfer.secondLegStart) / 2)}%`,
                }}
              >
                {transfer.hubFlag} {transfer.hubCode}
              </span>
            </>
          ) : (
            <Tooltip
              content={`Real · ETD ${formatOffset(vessel.realEtd, true)} → ETA ${formatOffset(vessel.actualEta, true)}`}
            >
              <span
                className={cn(
                  'absolute top-7 h-4 rounded-full',
                  pending && 'border border-dashed border-default-500'
                )}
                style={{
                  left: `${scale.percent(vessel.realEtd)}%`,
                  width: `${widthFor(vessel.realEtd, vessel.actualEta)}%`,
                  background: directBackground,
                }}
              />
            </Tooltip>
          )}

          {scale.percent(vessel.realEtd) >= 4 && (
            <span
              className='absolute top-9 -translate-x-[calc(100%+8px)] -translate-y-1/2 whitespace-nowrap text-[11px] text-default-400'
              style={{ left: `${scale.percent(vessel.realEtd)}%` }}
            >
              {formatOffset(vessel.realEtd)}
            </span>
          )}
          <span
            className='absolute top-9 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-default-600'
            style={{ left: `calc(${scale.percent(vessel.actualEta)}% + 8px)` }}
          >
            {formatOffset(vessel.actualEta)}
            {delay > 0 && (
              <Chip
                size='sm'
                variant='flat'
                color={delay >= 2 ? 'danger' : 'warning'}
                className='h-5'
              >
                +{delay}d
              </Chip>
            )}
          </span>
          {inTransit && (
            <Tooltip content='Posición estimada hoy'>
              <span
                className='absolute top-9 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background ring-4 ring-brand-500/50'
                style={{ left: `${scale.percent(0)}%` }}
              />
            </Tooltip>
          )}
        </div>
      </button>

      {expanded && (
        <div className='grid gap-x-7 gap-y-5 border-t border-dashed border-divider bg-default-50/60 px-5 py-5 sm:grid-cols-2 lg:grid-cols-4'>
          {details.map(([label, value]) => (
            <div key={label}>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                {label}
              </p>
              <p
                className={cn(
                  'mt-1 text-sm',
                  label === 'Ship Name' || label === 'Q Embarques'
                    ? 'font-semibold'
                    : '',
                  label === 'Real ETD' && vessel.realEtd !== vessel.originalEtd
                    ? 'text-warning'
                    : '',
                  label === 'Actual ETA' && delay > 0
                    ? delay >= 2
                      ? 'font-semibold text-danger'
                      : 'font-semibold text-warning'
                    : ''
                )}
              >
                {value}
              </p>
            </div>
          ))}
          {transfer && (
            <>
              <div>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  Tramo 1
                </p>
                <p className='mt-1 text-sm'>{vessel.vessel}</p>
                <p className='mt-0.5 text-xs text-default-400'>
                  {formatOffset(vessel.realEtd, true)} →{' '}
                  {formatOffset(transfer.firstLegEnd, true)}
                </p>
              </div>
              <div>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  Transbordo
                </p>
                <p className='mt-1 text-sm'>
                  {transfer.hubFlag} {transfer.hubName}
                </p>
                <p className='mt-0.5 text-xs text-default-400'>
                  {transfer.secondLegStart - transfer.firstLegEnd} días en
                  puerto
                </p>
              </div>
              <div>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  Tramo 2
                </p>
                <p className='mt-1 text-sm'>{transfer.secondVessel}</p>
                <p className='mt-0.5 text-xs text-default-400'>
                  {formatOffset(transfer.secondLegStart, true)} →{' '}
                  {formatOffset(vessel.actualEta, true)}
                </p>
              </div>
              <div>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  Atraso
                </p>
                <p
                  className={cn(
                    'mt-1 text-sm font-semibold',
                    delay > 0
                      ? delay >= 2
                        ? 'text-danger'
                        : 'text-warning'
                      : 'text-success'
                  )}
                >
                  {delay > 0
                    ? `+${delay}d en transbordo ${transfer.hubShortName}`
                    : 'Sin atraso'}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SchedulePage = () => {
  const { userProfile } = useUserStore();
  const isDemoExporter =
    userProfile?.exporter?.name?.trim().toUpperCase() === 'DEMO';
  const [sort, setSort] = useState<ScheduleSort>('ETD real');
  const [showOriginalPlan, setShowOriginalPlan] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('hace 20 min');

  const scale = useMemo(createScale, []);
  const months = useMemo(() => buildMonthBlocks(scale), [scale]);
  const weeks = useMemo(() => buildWeekMarkers(scale), [scale]);
  const sortedVessels = useMemo(
    () =>
      [...SCHEDULED_VESSELS].sort((a, b) => {
        if (sort === 'ETA real') return a.actualEta - b.actualEta;
        if (sort === 'Atraso') return delayFor(b) - delayFor(a);
        return a.realEtd - b.realEtd;
      }),
    [sort]
  );
  const inTransitCount = SCHEDULED_VESSELS.filter(
    (vessel) => vessel.actualEta >= 0 && vessel.realEtd <= 0
  ).length;
  const delayedCount = SCHEDULED_VESSELS.filter(
    (vessel) => delayFor(vessel) > 0
  ).length;
  const shipmentCount = SCHEDULED_VESSELS.reduce(
    (total, vessel) => total + vessel.shipmentCount,
    0
  );

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.setTimeout(() => {
      setIsRefreshing(false);
      setLastUpdated('ahora');
      toast.success('Tracking actualizado correctamente');
    }, 700);
  };

  if (!isDemoExporter) return <AccessDeniedPage />;

  return (
    <PageLayout className='h-screen overflow-y-auto'>
      <div className='mx-auto w-full max-w-[1800px]'>
        <PageHeader
          title='Cronograma'
          titleAddon={
            <Chip size='sm' variant='flat' color='warning'>
              Demo · datos de ejemplo
            </Chip>
          }
          description='Vista visual de cada nave: plan original vs. fechas reales de zarpe y arribo, puerto de destino y embarques a bordo.'
          actions={
            <div className='flex flex-wrap items-center gap-3'>
              <span className='text-xs text-default-400'>
                {SCHEDULED_VESSELS.length} naves · {inTransitCount} en tránsito ·{' '}
                {delayedCount} con atraso · {shipmentCount} embarques
              </span>
              <span className='text-xs text-default-400'>
                Actualizado {lastUpdated}
              </span>
              <Button
                size='sm'
                variant='light'
                color='danger'
                isLoading={isRefreshing}
                startContent={
                  !isRefreshing && <Icon icon='tabler:refresh' width={17} />
                }
                onPress={handleRefresh}
              >
                Actualizar tracking
              </Button>
            </div>
          }
        />

        <Card shadow='sm' className='mt-6'>
          <CardBody className='flex flex-row flex-wrap items-center gap-5 p-4'>
            <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
              Ordenar por
            </span>
            <div className='flex gap-1 rounded-xl bg-default-100 p-1'>
              {(['ETD real', 'ETA real', 'Atraso'] as ScheduleSort[]).map(
                (option) => (
                  <Button
                    key={option}
                    size='sm'
                    variant={sort === option ? 'solid' : 'light'}
                    color={sort === option ? 'primary' : 'default'}
                    className='h-8 min-w-0 px-3 font-semibold'
                    onPress={() => setSort(option)}
                  >
                    {option}
                  </Button>
                )
              )}
            </div>
            <Button
              size='sm'
              variant={showOriginalPlan ? 'solid' : 'flat'}
              color={showOriginalPlan ? 'primary' : 'default'}
              startContent={
                <span className='h-2 w-6 rounded border border-dashed border-default-500 bg-default-100' />
              }
              onPress={() => setShowOriginalPlan((value) => !value)}
            >
              Plan original
            </Button>
            <div className='ml-auto flex flex-wrap items-center gap-x-5 gap-y-2'>
              <LegendItem label='En tránsito' kind='transit' />
              <LegendItem label='Arribada' kind='arrived' />
              <LegendItem label='Por zarpar' kind='pending' />
              <LegendItem label='Transbordo' kind='transfer' />
              <LegendItem label='Hoy' kind='today' />
            </div>
          </CardBody>
        </Card>

        <Card shadow='sm' className='mt-4'>
          <CardBody className='overflow-x-auto p-0'>
            <div className='min-w-[1180px]'>
              <TimelineHeader scale={scale} months={months} weeks={weeks} />
              <div className='relative'>
                {sortedVessels.map((vessel) => {
                  const key = `${vessel.vessel}:${vessel.realEtd}`;
                  return (
                    <VesselRow
                      key={key}
                      vessel={vessel}
                      scale={scale}
                      showOriginalPlan={showOriginalPlan}
                      expanded={!!expanded[key]}
                      onToggle={() =>
                        setExpanded((current) => ({
                          ...current,
                          [key]: !current[key],
                        }))
                      }
                    />
                  );
                })}
              </div>
            </div>
          </CardBody>
        </Card>

        <p className='py-6 text-center text-xs text-default-400'>
          Escala {formatOffset(scale.start, true)} →{' '}
          {formatOffset(scale.end, true)} · fechas según último tracking ·
          selecciona una nave para ver el detalle
        </p>
      </div>
    </PageLayout>
  );
};

export default SchedulePage;
